import React, {useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Container, Divider, Link, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Image from '../components/Image.js';
import {withTitle} from '../title.js';
import withMainContainer from '../components/WithMainContainer.js';
import SCButton from '../components/SCButton.js';

const SERVICES = [{
    title: 'פגישת ייעוץ אישית',
    desc: `
    מרגישה תקועה? צריכה בהירות לגבי זוגיות, קריירה או כל נושא אחר?
    בפגישה אישית איתי (בטלפון או בזום), תקבלי תשובות, בהירות וכלים שיעזרו לך להבין את המצב ואיך לשנות אותו.
    בעזרת טארוט, נומרולוגיה ואסטרולוגיה, נבדוק מה באמת קורה מתחת לפני השטח - ומה אפשר לעשות כדי להתקדם.
    `,
    link: '/meeting',
    image: 'service-tarot.png',
}, {
    title: 'ליווי עסקי ואסטרטגיה הוליסטית',
    desc: `
    מרגישה שהעסק שלך לא באמת זורם?
    אולי את בתחילת הדרך ואין לך מושג מאיפה להתחיל - או שאת כבר בפנים, אבל לא רואה תוצאות כמו שציפית.
    בליווי שלי נבנה יחד אסטרטגיה ברורה לעסק שלך - שיווק, תמחור, תוכן וניהול - ובעזרת שילוב כלים רוחניים נוכל לראות את כל התמונה: החסמים, הבליינד ספוטס, וההזדמנויות.
    מתוך זה נבנה דרך שמדויקת רק לך - ושמביאה אותך להגשמה ולפרנסה כמו שמגיע לך.
    `,
    link: '/counseling',
    image: 'service-business.png',
}, {
    title: 'סדנאות ומעגלים - חיבור בין רוח לחומר',
    desc: `
    אני מעבירה סדנאות ומעגלים חווייתיים לקבוצות, ארגונים וא.נשים - חוויה שמשלבת עומק, הקשבה וצחוק, ומותאמת בדיוק לאנרגיה של הקבוצה.  
    בין אם מדובר בערב נשים, מעגל חברתי או סדנה מעשית - כל מפגש הוא מרחב פתוח לשיתוף, תובנות וחיבור, שמותיר את המשתתפים עם השראה, הקלה וכיוון חדש.
    `,
    link: '/workshops',
    image: 'service-workshop.png',
}, {
    title: 'שיעורים מכושפים אחד על אחד - לעבודה עם יכולות רוחניות',
    desc: `
    מרגישה שמשהו בתוכך נמשך למה שמעבר?
    רוצה להבין, לחקור ולחוות את העולם הרוחני והמיסטי מקרוב - בדרך אישית, בטוחה ומעשית?
    במפגשים פרטיים איתי בזום תוכלי ללמוד לתקשר, לעבוד עם מטוטלת ומוטות תקשור, ליצור מרחב אנרגטי אישי, להכיר חומרים לטיהור, הגנה ולברכה, ולהכין קמעות ונרות מכושפים - ועוד.
    זה המקום הבטוח לשאול את כל השאלות שאין לך את מי לשאול.
    השיעורים מותאמים בדיוק למקום שאת נמצאת בו -
    ושופכים אור על מה שלפעמים מרגיש מעורפל או מפחיד.
    `,
    link: '/private_lessons',
    image: 'service-witchcraft.png',
}];

const ServiceTitle = props=>{
    let {link, title} = props;
    let navigate = useNavigate();
    return <Link color="inherit"
      underline="hover"
      variant="h6"
      onClick={()=>navigate(link)}
      sx={{cursor: 'pointer'}}>
      {title}
    </Link>;
};

const MobileServiceContainer = props=>{
    let {title, link, image, descComp} = props;
    let navigate = useNavigate();
    return <Stack direction="column" gap={2} alignItems="center">
      <Image imagekit_src={image} maxWidth="100px" onClick={()=>navigate(link)}
        sx={{objectFit: 'contain', cursor: 'pointer'}}/>
      <ServiceTitle title={title} link={link}/>
      <Stack>{descComp}</Stack>
      <Stack alignItems="end" width="100%">
        <SCButton text="לפרטים נוספים" variant="outlined"
          icon={<ChevronLeftIcon/>} iconPlacement="right" onClick={()=>navigate(link)}/>
      </Stack>
    </Stack>
};

const ServiceContainer = props=>{
    let {title, link, image, desc} = props;
    let navigate = useNavigate();
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let descComp = useMemo(()=>desc.split(/\s*\n\s*/)
        .map(s=>s.trim())
        .filter(s=>s?.length)
        .map((s, i)=><Typography key={i}>{s}</Typography>), [desc]);
    if (sm)
        return <MobileServiceContainer title={title} link={link} image={image} descComp={descComp}/>;
    return <Stack direction="row" gap={3} alignItems="start" width="100%">
      <Image imagekit_src={image} maxWidth="120px" onClick={()=>navigate(link)}
        sx={{objectFit: 'contain', cursor: 'pointer'}}/>
      <Stack direction="column" gap={2} flexGrow={1}>
        <ServiceTitle title={title} link={link}/>
        <Stack>{descComp}</Stack>
        <Stack alignItems="start">
          <SCButton text="לפרטים נוספים" variant="outlined"
            icon={<ChevronLeftIcon/>} iconPlacement="right" onClick={()=>navigate(link)}/>
        </Stack>
      </Stack>
    </Stack>;
};

const Services = ()=>{
    return <Container maxWidth="md">
      <Stack direction="column" gap={3} marginBottom={4} alignItems="center">
        <Typography variant="h3">השירותים שלי</Typography>
        <Typography textAlign="center">
          עוזרת לך לראות את מה שלא רואים לבד - ולבנות מציאות שמדויקת רק לך.<br/>
          כאן תמצאי מגוון שירותים, מפגישות אישיות ועד סדנאות קבוצתיות - תוכלי לבחור את מה שהכי מתאים לך עכשיו.
        </Typography>
        <Divider/>
      </Stack>
      <Stack direction="column" gap={4}>
        {SERVICES.map((s, i)=><React.Fragment key={i}>
          {i!=0&&<Divider/>}
          <ServiceContainer {...s}/>
        </React.Fragment>)}
      </Stack>
    </Container>;
};

export default withMainContainer(withTitle(`סימונה צ'רי - השירותים שלי`, Services), {footerMargin: 4});
